import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  usePreviewData,
  UsePreviewDataOptions,
} from "@/features/previews/shared/hooks/usePreviewData";
import { QRProps } from "@/contexts/QREditorContext/types";
import { PAGES } from "@/internals/constants/pages";
import { getLanguageCookie } from "@/services/LanguagePreferenceService";
import { getFullAddress } from "@/utils/qrEditorHelper";
import { checkValidURL } from "@/utils/formHelper";
import { TimezonesService } from "@/services/TimezonesService";
import { LocationType } from "@/components/PreviewLocationBlock/types";
import { LabeledDataType } from "@/components/LabeledData/types";
import { TCalendarEvent } from "@/services/AddToCalendarService/types";
import { QREventType } from "@/types/qr";
import { Link } from "@/components/Link";

export function useEventData(props: QRProps<"event">, options?: UsePreviewDataOptions) {
  const { data, isInitPreview, hasContext } = usePreviewData(props, options);
  const { t } = useTranslation(PAGES.QR_EVENT);
  const locale = getLanguageCookie();

  const formattedData = useMemo(() => {
    if (!data) return null;

    const {
      eventDateTime,
      logoUrl,
      infoTitle,
      infoDescription,
      infoActionTitle,
      infoActionUrl,
      themeColor,
      facilities,
      locationAddress,
      locationNumeration,
      locationPostalCode,
      locationCity,
      locationState,
      locationCountry,
      organizerName,
      organizerPhone,
      organizerEmail,
      organizerWebsite,
      organizerAbout,
    } = data as QREventType;

    const addressData: LocationType = {
      address: locationAddress,
      numeration: locationNumeration,
      zipCode: locationPostalCode,
      city: locationCity,
      state: locationState,
      country: locationCountry,
      fullAddress: getFullAddress(
        locationAddress,
        locationNumeration,
        locationPostalCode,
        locationCity,
        locationState,
        locationCountry
      ),
    };

    const organizerData: LabeledDataType[] = [
      {
        label: t("preview_organizer_name"),
        value: organizerName,
      },
      {
        label: t("preview_organizer_phone"),
        value: organizerPhone,
      },
      {
        label: t("preview_organizer_email"),
        value: organizerEmail ? (
          <Link
            externalLink
            className="business-link"
            href={`mailto:${organizerEmail}`}
            prefetch={false}
          >
            {organizerEmail}
          </Link>
        ) : (
          ""
        ),
      },
      {
        label: t("preview_organizer_website"),
        value: organizerWebsite ? (
          <Link
            externalLink
            className="business-link"
            href={checkValidURL(organizerWebsite) || ""}
            prefetch={false}
          >
            {organizerWebsite}
          </Link>
        ) : (
          ""
        ),
      },
      {
        label: t("preview_organizer_about"),
        value: organizerAbout,
      },
    ];

    const calendarEvent: TCalendarEvent = {
      name: infoTitle || t("preview_event_name"),
      details: infoDescription || "",
      location: addressData?.fullAddress,
      startsAt: TimezonesService.convertDateFromTimezoneToLocalTimezone(
        eventDateTime?.start?.date,
        eventDateTime?.allDay ? "0:00" : eventDateTime?.start?.time,
        eventDateTime?.timezone
      ),
      endsAt: TimezonesService.convertDateFromTimezoneToLocalTimezone(
        eventDateTime?.end?.date,
        eventDateTime?.allDay ? "23:59" : eventDateTime?.end?.time,
        eventDateTime?.timezone
      ),
      timezone: eventDateTime?.timezone,
      allDay: eventDateTime?.allDay,
    };

    return {
      eventDateTime,
      logoUrl,
      infoTitle,
      infoDescription,
      infoActionTitle,
      infoActionUrl,
      themeColor,
      facilities,
      addressData,
      organizerData,
      calendarEvent,
    };
  }, [data, t]);

  return {
    data: formattedData,
    rawQrData: data,
    isInitPreview,
    hasContext,
    locale,
    t,
  };
}
